/**
 * @file: downloadMedias.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { MediaWithSource } from '@euler/model';
import { usePersistFn } from '@euler/utils/hooks';
import { useState } from 'react';
import { downloadMediasImpl } from './downloadMediasImpl';

type DownloadCompleteArgs = {
  total: number;
  success: number;
  failed: number;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useDownloadMedias = ({
  zipFilename,
  onPermissionDenied,
  onDownloadComplete,
  onDownloadError,
}: {
  zipFilename: string;
  onPermissionDenied?: (msg: string) => void;
  onDownloadComplete?: (args: DownloadCompleteArgs) => void;
  onDownloadError?: (error: unknown) => void;
}) => {
  const [downloaded, setDownloaded] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [failed, setFailed] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);
  const [downloading, setDownloading] = useState(false);

  const stableDownloadComplete = usePersistFn((args: DownloadCompleteArgs) => {
    onDownloadComplete?.(args);
  });

  const downloadMedias = usePersistFn(async (medias: MediaWithSource[]) => {
    setTotal(medias.length);
    setDownloaded(0);
    setFailed(0);
    setProgress(0);

    setDownloading(true);

    try {
      let successCount = 0;
      let errorCount = 0;

      await downloadMediasImpl(medias, {
        zipFilename,
        onPermissionDenied,
        onBeforeDownload: () => {
          setProgress(prev => prev + 1);
        },
        onSuccess: () => {
          setDownloaded(prev => prev + 1);

          successCount++;
        },
        onError: err => {
          console.error('Download media error:', err);
          setFailed(prev => prev + 1);
          errorCount++;
        },
        onComplete: () => {
          stableDownloadComplete({
            total: medias.length,
            success: successCount,
            failed: errorCount,
          });
        },
      });
    } catch (e) {
      onDownloadError?.(e);
    } finally {
      setDownloading(false);
    }
  });

  return {
    downloadMedias,
    downloadStats: {
      downloading,
      downloaded,
      total,
      failed,
      progress,
    },
  };
};
